<template>
  <div class="contenedor">
    <div class="encabezado" v-if="filasFiltradas.length">
      <div class="info-empresa">
        <h1>Oferta Caracas</h1>
        <p>Cierre de Caja</p>
        <p>Del {{ formatearFecha(fechaInicio) }} al {{ formatearFecha(fechaFin) }}</p>
        <p v-if="usuarioNombre">Usuario: {{ usuarioNombre }}</p>
        <p v-if="almacenNombre">Almacén: {{ almacenNombre }}</p>
        <p v-if="negocioNombre">Negocio: {{ negocioNombre }}</p>
      </div>
      <hr>
    </div>

    <!-- Resumen por metodo de pago -->
    <div v-if="resumenMetodos.length" class="resumen">
      <h2>Resumen por medio de pago</h2>
      <table class="tabla-resumen">
        <thead>
          <tr>
            <th>Medio de pago</th>
            <th class="monto">Total</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in resumenMetodos" :key="item.metodo_pago">
            <td>{{ item.metodo_pago }}</td>
            <td class="monto">${{ formatoMonto(item.total) }}</td>
          </tr>
          <tr class="total-row">
            <td><strong>TOTAL GENERAL</strong></td>
            <td class="monto"><strong>${{ formatoMonto(totalGeneral) }}</strong></td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Detalle de transacciones -->
    <div v-if="filasFiltradas.length" class="detalle">
      <h2>Detalle de transacciones</h2>
      <table class="tabla-detalle">
        <thead>
          <tr>
            <th>Cliente</th>
            <th class="monto">Total</th>
            <th v-for="metodo in metodosColumnas" :key="metodo" class="monto">{{ metodo }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(fila, i) in filasConMontosPorMetodo" :key="i">
            <td>{{ fila.cliente }}</td>
            <td class="monto">${{ formatoMonto(fila.total) }}</td>
            <td v-for="metodo in metodosColumnas" :key="metodo" class="monto">
              {{ fila.metodos[metodo] ? '$' + formatoMonto(fila.metodos[metodo]) : '-' }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="!filasFiltradas.length" class="sin-datos">
      <p>No se encontraron transacciones para el período seleccionado.</p>
    </div>
  </div>
</template>

<script>
import HttpService from '@/Servicios/HttpService'
import Utiles from '@/Servicios/Utiles'

export default {
  name: 'PDFCierres',

  data: () => ({
    fechaInicio: null,
    fechaFin: null,
    usuarioId: null,
    usuarioNombre: null,
    almacenId: null,
    almacenNombre: null,
    negocioId: null,
    negocioNombre: null,
    metodos: [],
    filas: [],
    distribucionAlmacenes: {},
    ajustes: null,
  }),

  computed: {
    // Filtrar filas y montos según almacén seleccionado
    filasFiltradas() {
      if (!this.almacenId) return this.filas
      return this.filas.filter(f => {
        const key = f.payment_type + '_' + f.id_referencia
        const dist = this.distribucionAlmacenes[key]
        return dist && dist[this.almacenId] !== undefined && parseFloat(dist[this.almacenId]) > 0
      })
    },

    // Resumen por método de pago (monto del almacén filtrado distribuido proporcional)
    resumenMetodos() {
      const totals = {}
      this.filasFiltradas.forEach(f => {
        const montoFiltrado = this.montoMostrar(f)
        if (montoFiltrado <= 0) return

        // Parsear desglose: ["Efectivo ($): 60.00", "Transferencia: 40.00"]
        const lineas = String(f.desglose_pagos || '').split(' + ')
        const montosMetodo = []
        let totalDesglose = 0
        lineas.forEach(linea => {
          const parts = linea.split(': ')
          if (parts.length === 2) {
            const m = parseFloat(parts[1].replace(/[$,]/g, ''))
            montosMetodo.push({ metodo: parts[0], monto: m })
            totalDesglose += m
          }
        })

        if (totalDesglose <= 0) return

        // Distribuir el monto del almacén proporcionalmente entre los métodos
        montosMetodo.forEach(({ metodo, monto }) => {
          const parte = montoFiltrado * (monto / totalDesglose)
          if (!totals[metodo]) totals[metodo] = 0
          totals[metodo] += parte
        })
      })
      return Object.entries(totals).map(([metodo_pago, total]) => ({
        metodo_pago,
        total: Math.round(total * 100) / 100,
      })).sort((a, b) => a.metodo_pago.localeCompare(b.metodo_pago))
    },
    totalGeneral() {
      return this.resumenMetodos.reduce((sum, item) => sum + parseFloat(item.total || 0), 0)
    },

    // Columnas: métodos activos del módulo + métodos históricos presentes en filas
    metodosColumnas() {
      const set = new Set()
      ;(this.metodos || []).forEach(m => { if (m && m.nombre) set.add(m.nombre) })
      this.filasFiltradas.forEach(f => {
        this.obtenerMontosPorMetodo(f).forEach(({ metodo }) => set.add(metodo))
      })
      return [...set].sort((a, b) => a.localeCompare(b))
    },

    // Filas con total + desglose por método (proporcional si hay almacén filtrado)
    filasConMontosPorMetodo() {
      return this.filasFiltradas.map(f => {
        const total = this.montoMostrar(f)
        const entradas = this.obtenerMontosPorMetodo(f)
        const totalDesglose = entradas.reduce((s, e) => s + e.monto, 0)
        const montos = {}
        entradas.forEach(({ metodo, monto }) => {
          montos[metodo] = totalDesglose > 0 ? total * (monto / totalDesglose) : 0
        })
        return { cliente: f.cliente, total, metodos: montos }
      })
    },
  },

  mounted() {
    // El hash es "#/pdf/cierres?fechaInicio=...&fechaFin=..."
    const href = window.location.hash || ''
    const queryIndex = href.indexOf('?')
    const params = new URLSearchParams(queryIndex >= 0 ? href.slice(queryIndex + 1) : '')
    this.fechaInicio = params.get('fechaInicio') || ''
    this.fechaFin = params.get('fechaFin') || ''
    this.usuarioId = params.get('usuarioId') || null
    this.almacenId = params.get('almacenId') || null
    this.negocioId = params.get('negocioId') || null
    try {
      const ajustesRaw = params.get('ajustes')
      if (ajustesRaw) {
        this.ajustes = JSON.parse(ajustesRaw)
      }
    } catch (e) {
      console.warn('Error parsing ajustes:', e)
    }
    this.cargarDatos()
  },

  methods: {
    formatoMonto(valor) {
      return Utiles.formatoMonto(valor)
    },

    // Monto de la fila según el almacén filtrado
    montoMostrar(fila) {
      if (!this.almacenId) return fila.monto_total
      const key = fila.payment_type + '_' + fila.id_referencia
      const dist = this.distribucionAlmacenes[key] || {}
      return dist[this.almacenId] !== undefined ? dist[this.almacenId] : 0
    },

    // Parsear desglose_pagos "Efectivo ($): 60.00 + Transferencia: 40.00" → [{metodo, monto}]
    obtenerMontosPorMetodo(fila) {
      const lineas = String(fila.desglose_pagos || '').split(' + ')
      const resultado = []
      lineas.forEach(linea => {
        const parts = linea.split(': ')
        if (parts.length === 2) {
          resultado.push({ metodo: parts[0], monto: parseFloat(parts[1].replace(/[$,]/g, '')) })
        }
      })
      return resultado
    },

    formatearFecha(fecha) {
      if (!fecha) return ''
      const d = new Date(fecha)
      return d.toLocaleDateString('es-VE', { year: 'numeric', month: '2-digit', day: '2-digit' })
    },

    async cargarDatos() {
      const filtros = {
        fechaInicio: this.fechaInicio,
        fechaFin: this.fechaFin,
        idNegocio: this.negocioId || null,
      }

      try {
        const detalle = await HttpService.obtenerConConsultas('cierres.php', {
          accion: 'obtener',
          filtros,
          usuarioId: this.usuarioId || null,
        })
        this.filas = detalle || []

        // Parsear distribución automática desde el API
        this.distribucionAlmacenes = {}
        this.filas.forEach(f => {
          if (f.distribucion_almacenes) {
            const key = f.payment_type + '_' + f.id_referencia
            const dist = {}
            String(f.distribucion_almacenes).split('|').forEach(part => {
              const [idAlmacen, monto] = part.split(':')
              if (idAlmacen && monto) dist[idAlmacen] = parseFloat(monto)
            })
            if (Object.keys(dist).length > 0) {
              this.distribucionAlmacenes[key] = dist
            }
          }
        })

        // Aplicar distribuciones manuales del usuario (temporales, desde la URL)
        if (this.ajustes && this.ajustes.distribuciones) {
          Object.entries(this.ajustes.distribuciones).forEach(([key, dist]) => {
            this.distribucionAlmacenes[key] = dist
          })
        }

        // Cargar métodos de pago (para columnas fijas)
        const metodosRes = await HttpService.obtenerConConsultas('metodos.php', { accion: 'obtener' })
        this.metodos = metodosRes || []

        // Cargar almacenes para mostrar el nombre del filtrado
        const almacenes = await HttpService.obtenerConConsultas('almacenes.php', { accion: 'obtener' })
        this.almacenes = almacenes || []
        if (this.almacenId) {
          const a = this.almacenes.find(a => String(a.id) === String(this.almacenId))
          if (a) this.almacenNombre = `${a.nombre} (${a.cod})`
        }

        if (this.usuarioId) {
          const usuarios = await HttpService.obtenerConConsultas('usuarios.php', { accion: 'obtener' })
          const u = (usuarios || []).find(u => u.id == this.usuarioId)
          if (u) this.usuarioNombre = u.usuario
        }

        if (this.negocioId) {
          const negocios = await HttpService.obtenerConConsultas('negocios.php', { accion: 'obtener' })
          const n = (negocios || []).find(n => String(n.id) === String(this.negocioId))
          if (n) this.negocioNombre = n.nombre
        }
      } catch (e) {
        this.filas = []
      }

      setTimeout(() => window.print(), 500)
    },
  },
}
</script>

<style scoped>
.contenedor {
  max-width: 1000px;
  margin: 0 auto;
  padding: 20px;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}
.encabezado {
  text-align: center;
  margin-bottom: 20px;
}
.encabezado h1 {
  font-size: 24px;
  margin: 0;
}
.encabezado p {
  margin: 2px 0;
  color: #555;
}
.resumen, .detalle {
  margin-top: 20px;
}
h2 {
  font-size: 18px;
  margin-bottom: 10px;
  border-bottom: 2px solid #333;
  padding-bottom: 5px;
}
table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 20px;
}
th, td {
  border: 1px solid #ddd;
  padding: 6px 10px;
  text-align: left;
  font-size: 12px;
}
th {
  background: #f5f5f5;
  font-weight: bold;
}
.monto {
  text-align: right;
}
.total-row {
  background: #ffe082;
  font-weight: bold;
}
.sin-datos {
  text-align: center;
  color: #999;
  margin-top: 40px;
}
@media print {
  .contenedor {
    padding: 0;
  }
  th {
    background: #eee !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
}
</style>